import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiGet, apiPost, apiPut, apiDelete } from "../../services/api";
import type { ProductCategory } from "../../types";

export default function CategoriesTab() {
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", description: "" });

  useEffect(() => { if (orgId) loadCategories(); }, [orgId]);

  async function loadCategories() {
    try { setCategories(await apiGet<ProductCategory[]>(`/organizations/${orgId}/categories`)); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  const openCreate = () => { setEditing(null); setForm({ name: "", description: "" }); setShowModal(true); };
  const openEdit = (cat: any) => { setEditing(cat.categoryId); setForm({ name: cat.name || "", description: cat.description || "" }); setShowModal(true); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editing) {
      await apiPut(`/organizations/${orgId}/categories/${editing}`, form);
    } else {
      await apiPost(`/organizations/${orgId}/categories`, form);
    }
    setShowModal(false);
    await loadCategories();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this category?")) return;
    await apiDelete(`/organizations/${orgId}/categories/${id}`);
    await loadCategories();
  };

  if (loading) return <div className="empty-state">Loading categories...</div>;

  return (
    <div>
      <div className="org-mgmt-header">
        <h2>Categories</h2>
        <button className="btn btn-primary" style={{ width: "auto" }} onClick={openCreate}>+ Add Category</button>
      </div>

      {categories.length === 0 ? (
        <div className="empty-state"><p>No categories yet.</p></div>
      ) : (
        <table className="data-table">
          <thead><tr><th>Name</th><th>Description</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {categories.map((cat: any) => (
              <tr key={cat.categoryId}>
                <td style={{ fontWeight: 500 }}>{cat.name}</td>
                <td style={{ color: "#6b7280" }}>{cat.description || "—"}</td>
                <td><span className={`status-badge ${cat.status}`}>{cat.status}</span></td>
                <td><div className="table-actions"><button onClick={() => openEdit(cat)}>Edit</button><button className="delete" onClick={() => handleDelete(cat.categoryId)}>Delete</button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editing ? "Edit Category" : "Add Category"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="settings-form">
                <div className="form-group"><label>Name</label><input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required /></div>
                <div className="form-group"><label>Description</label><input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ width: "auto" }}>{editing ? "Save Changes" : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
